import { AxiosInstance, AxiosPromise, AxiosRequestConfig, AxiosResponse } from 'axios';
import { stringify } from 'query-string';
import { CycleI, PackName } from '~src/interfaces/cycle.interface';
import { CardI } from '~src/interfaces/question.interface';
import { Segment } from '~src/interfaces/segment.interface';

import { FetchScalesParams, ScalesRespI, ScalesSortParam } from '~src/interfaces/scale.interface';
import { ScheduleI, ScheduleUpdate } from '~src/interfaces/schedule.interface';
import { Topic } from '~src/interfaces/topic.interface';
import { CardResponse } from '~src/modules/reports/interfaces/answer.interface';
import { FetchTopicsForSurveyParams } from '~src/modules/surveys/actions/topicActions';
import {
  CreateTeamSurveyResp,
  CreateUserPullSurveySubmitData,
  FetchSurveysParams,
  NewManagerSurveySubmitData,
  NewSurveySubmitData, NewTeamSurveySubmitData,
  Survey,
  SurveyExtended, SurveyScheduleResponse,
} from '~src/modules/surveys/interfaces';
import { getUrlFromObject } from '~src/utils';
import { FeedbackCycle, FeedbackCycleResponse } from '~src/modules/reports/interfaces/meetings.interfaces';
import { AdminFeedbackCycle, AdminFeedbackCycleResponse, AdminFeedbackEntriesResponse, GetAdminFeedbackEntryPayload, PostAdminFeedbackCyclePayload, PutAdminFeedbackCyclePayload } from '~src/hooks/api/adminFeedback/types';
import {PaginationI} from "~src/interfaces/pagination.interface";
import {FetchTopicsParams} from "~src/modules/topics/actions/topicActions";

// see models.ListRespnose in pulse-check-service
export interface ListResponse<T> {
  items: T[];
  paginaton: PaginationI;
  filters: Record<string, string[]>;
  include: string[];
  sorting: {field:string, order: string}[];
}

interface FetchQuestionsParams {
  cycleId: string;
  packName: PackName;
  precedent?: string; // return these "about" first
}

export interface SelectionType {
  asked?: Segment[] | null;
  about?: Segment;
  managerSelection?: Segment[];
  selfSelection?: Segment[];
  customSelection?: Segment[];
}

export interface ManagersSelectionPostData {
  about_id: string;
  ask_ids: string[];
  cycle_id: string;
}

export interface UpdateSelfSelection {
  about_id?: string;
  ask_ids: string[];
  cycle_id: string;
}

export interface FeedbackSurveyList {
  page?: number;
  sort?: string;
  per_page: number,

}

export interface PutFeedbackCyclePayload {
  cycleId: string;
  dueDate: string;
  startDate: string;
}
export interface PostFeedbackCyclePayload {
  userId: string;
  dueDate: string;
  startDate: string;
}

export function encodePulseCheckQueryString(query: Record<string, string[]>): string {
  const queryString = Object.keys(query)
    .map((name: string) => {
      return [name, query[name].map(encodeURIComponent).join(',')].join('=');
    })
    .join('&');

  return queryString ? '?' + queryString : '';
}

export class PulseCheck {
  constructor(private axios: AxiosInstance, private abortSignal?: AbortSignal) {}

  withSignal(abortSignal: AbortSignal) {
    return new PulseCheck(this.axios, abortSignal);
  }

  getSurvey(id: string): AxiosPromise<Survey> {
    return this.axios.get(`/surveys/${id}`);
  }

  getSurveyTopics(surveyId: string, params: FetchTopicsForSurveyParams): AxiosPromise<Topic[]> {
    return this.axios.get(`/surveys/${surveyId}/topics?${stringify(params)}`);
  }

  postSurvey(survey: NewSurveySubmitData): AxiosPromise<Survey> {
    return this.axios.post('/surveys', survey);
  }

  getVisibleSurveys(params: FetchSurveysParams, config?: AxiosRequestConfig): AxiosPromise {
    return this.axios.get(`/visible-surveys/?${stringify(params)}`, config);
  }

  postManagersSurvey(surveyAndSchedule: NewManagerSurveySubmitData): Promise<AxiosResponse<SurveyScheduleResponse>> {
    return this.axios.post('/managers-surveys', surveyAndSchedule);
  }

  postTeamSurvey(data: NewTeamSurveySubmitData):Promise<AxiosResponse<CreateTeamSurveyResp>> {
    return this.axios.post('/team-surveys', data)
  }

  postUsersSurvey(data: CreateUserPullSurveySubmitData): Promise<AxiosResponse<SurveyScheduleResponse>> {
    return this.axios.post('/users-surveys', data);
  }

  putSurvey(id: string, formData: NewSurveySubmitData) {
    return this.axios.put(`/surveys/${id}`, formData);
  }

  deleteSurvey(id: string) {
    return this.axios.delete(`/surveys/${id}`);
  }

  editSurvey(id: string, formData: Record<string, unknown>) {
    return this.axios.put(`/surveys/${id}`, formData);
  }

  getTopic(id: string): Promise<AxiosResponse<Topic>> {
    return this.axios.get(`/topics/${id}`);
  }

  getTopics(params: FetchTopicsParams = {}): Promise<AxiosResponse<ListResponse<Topic>>> {
    return this.axios.get(`/topics${generateQueryParams({...params})}`);
  }

  getManagersSelection(cycleId: string, precedent?: string): Promise<AxiosResponse<SelectionType[]>> {
    return this.axios.get(
      `/cycles/${cycleId}/custom-participants/manager${precedent ? `?precedent=${precedent}` : ''}`,
      { signal: this.abortSignal },
    );
  }

  getSelfSelection(cycleId: string): Promise<AxiosResponse<SelectionType>> {
    return this.axios.get(`/cycles/${cycleId}/custom-participants/self`, { signal: this.abortSignal });
  }

  getCustomSelection(cycleId: string, precedent?: string): Promise<AxiosResponse<SelectionType[]>> {
    return this.axios.get(`/cycles/${cycleId}/custom-participants/custom?precedent=${precedent}`, {
      signal: this.abortSignal,
    });
  }

  postManagersSelection(selection: ManagersSelectionPostData): Promise<AxiosResponse<void>> {
    return this.axios.put('/custom-participants/manager', selection);
  }

  postSelfSelection(selection: UpdateSelfSelection): Promise<AxiosResponse<void>> {
    return this.axios.put('/custom-participants/self', selection, { signal: this.abortSignal });
  }

  postCustomSelection(selection: UpdateSelfSelection): Promise<AxiosResponse<void>> {
    return this.axios.put('/custom-participants/custom', selection, { signal: this.abortSignal });
  }

  getTopicQuestions(id: string) {
    return this.axios.get(`/topics/${id}/questions`);
  }

  getSurveySchedules(id: string): Promise<AxiosResponse<ScheduleI[]>> {
    return this.axios.get(`/surveys/${id}/schedules`);
  }

  getSchedule(id: string): Promise<AxiosResponse<ScheduleI>> {
    return this.axios.get(`/schedules/${id}`);
  }

  postSchedule(schedule: ScheduleUpdate, surveyId: string): Promise<AxiosResponse<ScheduleI>> {
    return this.axios.post(`/surveys/${surveyId}/schedules`, schedule);
  }

  putSchedule(schedule: ScheduleUpdate): Promise<AxiosResponse<ScheduleI>> {
    return this.axios.put(`/schedules/${schedule.id}`, schedule);
  }

  getScheduleCycles(scheduleId: string): Promise<AxiosResponse<CycleI[]>> {
    return this.axios.get(`/schedules/${scheduleId}/cycles`);
  }

  getScales(params?: FetchScalesParams): AxiosPromise<ScalesRespI> {
    const paramsMerged: FetchScalesParams = {
      sort: ScalesSortParam.CreatedDesc,
      ...params,
    };
    return this.axios.get(`/scales?${stringify(paramsMerged)}`);
  }

  getQuestions(params: FetchQuestionsParams): AxiosPromise<CardI[]> {
    const query: Record<string, string[]> = {
      'filter.type': [params.packName],
      sort: params.precedent
        ? [`-precedent=${params.precedent}`, 'about', 'answered']
        : ['about', 'answered'],
    };

    return this.axios.get(`/cycles/${params.cycleId}/cards${encodePulseCheckQueryString(query)}`);
  }

  getCycles(): AxiosPromise<CycleI[]> {
    return this.axios.get('/cycles?include=answered', { signal: this.abortSignal });
  }

  postCardResponse(args: { cardId: string; content: string }): AxiosPromise<CardResponse> {
    return this.axios.post(`/cards/${args.cardId}/responses`, { content: args.content });
  }

  getCardResponses(cardId: string): AxiosPromise<CardResponse[]> {
    return this.axios.get(`/cards/${cardId}/responses`, { signal: this.abortSignal });
  }

  updateCardResponse(args: {
    cardId: string;
    responseId: string;
    content: string;
  }): Promise<AxiosResponse<CardResponse>> {
    return this.axios.put(`/cards/${args.cardId}/responses/${args.responseId}`, { content: args.content });
  }

  deleteCardResponse(args: { cardId: string; responseId: string }): Promise<AxiosResponse<void>> {
    return this.axios.delete(`/cards/${args.cardId}/responses/${args.responseId}`);
  }

  postFeedbackCycle(args: {
    userId: string;
    startDate: string;
    dueDate: string;
  }): Promise<AxiosResponse<FeedbackCycle>> {
    return this.axios.post(`/feedback-cycles/user/${args.userId}`, {
      startDate: args.startDate,
      dueDate: args.dueDate,
    });
  }

  finalizeFeedbackCycleAsUser(cycleUuid: string): Promise<AxiosResponse<FeedbackCycle>> {
    return this.axios.put(`/feedback-cycles/${cycleUuid}/userFinished`)
  }
  finalizeFeedbackCycleAsManager(cycleUuid: string): Promise<AxiosResponse<FeedbackCycle>> {
    return this.axios.put(`/feedback-cycles/${cycleUuid}/managerFinished`)
  }

  getFeedbackCycles(
    args: { userId: string },
    config?: AxiosRequestConfig,
  ): AxiosPromise<FeedbackCycleResponse> {
    return this.axios.get(`/feedback-cycles/user/${args.userId}?page=1&per_page=10000&sort=-created`, config);
  }

  putFeedbackCycles(payload: PutFeedbackCyclePayload): AxiosPromise<FeedbackCycle> {
    return this.axios.put(`/feedback-cycles/${payload.cycleId}`, payload);
  }

  getFeedbackSurveys(
    args: { userId: string; filterParams: FetchSurveysParams },
    config?: AxiosRequestConfig,
  ): AxiosPromise<ListResponse<SurveyExtended>> {
    const params = getUrlFromObject(args.filterParams as Record<string, string>);
    return this.axios.get(`surveys/user/${args.userId}${params}`, config);
  }

  postAdminFeedbackCycle(args: PostAdminFeedbackCyclePayload): Promise<AxiosResponse<AdminFeedbackCycle>> {
    return this.axios.post(`/admin-feedback-cycles`, args);
  }

  getAdminFeedbackCycles(config?: AxiosRequestConfig): AxiosPromise<AdminFeedbackCycleResponse> {
    return this.axios.get(`/admin-feedback-cycles?page=1&per_page=10000&sort=-created`, config);
  }

  putAdminFeedbackCycle(
    payload: PutAdminFeedbackCyclePayload,
    config?: AxiosRequestConfig,
  ): AxiosPromise<AdminFeedbackCycle> {
    const { id, ...rest } = payload;
    return this.axios.put(`/admin-feedback-cycles/${id}`, rest, config);
  }

  deleteAdminFeedbackCycles(feedbackCycleId: string, config?: AxiosRequestConfig): AxiosPromise<void> {
    return this.axios.delete(`/admin-feedback-cycles/${feedbackCycleId}`, config);
  }

  postRemindUsers(feedbackCycleId: string, config?: AxiosRequestConfig): AxiosPromise<void> {
    return this.axios.post(`/admin-feedback-cycles/${feedbackCycleId}/remindUsers`, config);
  }

  getAdminFeedbackCycle(id: string, config?: AxiosRequestConfig): AxiosPromise<AdminFeedbackCycle> {
    return this.axios.get(`/admin-feedback-cycles/${id}`, config);
  }

  getAdminFeedbackEntries(
    { id, ...payload }: GetAdminFeedbackEntryPayload,
    config?: AxiosRequestConfig,
  ): AxiosPromise<AdminFeedbackEntriesResponse> {
    const query = generateQueryParams(payload);
    return this.axios.get(`/admin-feedback-cycles/${id}/feedback-cycles${query}`, config);
  }
}

function generateQueryParams(params: Record<string, string | number | boolean>) {
  const query = Object.entries(params)
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    .filter(([_, value]) => value || value === 0 || value === false) // Include only non-falsy, 0, or false values
    .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
    .join('&');
  return query ? `?${query}` : '';
}
